import { Component, OnInit, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Address } from '@models/address/address';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { EntityData } from '@models/companies/entity-data';
import { SecurityHolding } from '@models/shares/security-holding';
import { CorporateHolderModel, SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { NumbersValidators } from '@shared/validators/numbers.validators';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { Guid } from '@shared/utils/guid.helper';
import { BaseShareFormComponent } from '../base-share-form/base-share-form.component';
import {
  ShareEstimateTableComponent
} from '../../../reusable-form-parts/share-estimate-table/share-estimate-table.component';
import { ShareholderTypesEnum } from '../../../models/shares/company-security-issue.model';
import {
  CompanySecurityTransfer,
  CompanySecurityTransferStepsEnum,
  ShareTransferSteps
} from '../../../models/shares/company-security-transfer.model';
import {
  AuthorisedSignatoriesStepComponent
} from '../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component';
import { debounceTime } from 'rxjs';
import {
  InputCertificateNumberComponent
} from '@shared/controls/input-certificate-number/input-certificate-number.component';
import { ClientSecurityTransaction } from '@models/security-transactions/client-security-transaction';
import {
  CompanySecurityTransferFromBlock
} from "../company-security-transfer-from-block/share-transfer-from-block.component";
import {
  CompanySecurityTransferToBlock
} from '../company-security-transfer-to-block/share-transfer-to-block.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { reloadShareholdersDebounceTimeMs } from "../constants/reload-shareholders-delay";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import { groupSecurityRecordsByName } from "@shared/utils/security.helper";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";

@Component({
  selector: 'app-share-transfer',
  standalone: true,
    imports: [
        StepperFormComponent,
        ReactiveFormsModule,
        DatePickerComponent,
        ShareEstimateTableComponent,
        InputCertificateNumberComponent,
        SelectComponent,
        AuthorisedSignatoriesStepComponent,
        StepperFormDescriptionComponent,
        CompanySecurityTransferFromBlock,
        CompanySecurityTransferToBlock,
        DisclaimerComponent
    ],
  templateUrl: './company-security-transfer.component.html',
  styleUrls: [
    '../company-security-cancellation-block/share-cancellation-block.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    '../company-security-cancellation/company-security-cancellation.component.scss'
  ]
})
export class CompanySecurityTransferComponent extends BaseShareFormComponent<CompanySecurityTransferStepsEnum, CompanySecurityTransfer> implements OnInit {
  shareTransferFromBlock = viewChild(CompanySecurityTransferFromBlock);
  shareTransferToBlock = viewChild(CompanySecurityTransferToBlock);

  transactionsList: ClientSecurityTransaction[] = [];
  override StepsEnum = CompanySecurityTransferStepsEnum;
  override steps = ShareTransferSteps;
  override currentStepIndex = 0;
  override currentStep = this.steps[0].step;

  override stepperForm = new FormGroup({
    [CompanySecurityTransferStepsEnum.FormDescription]: new FormGroup({}),
    [CompanySecurityTransferStepsEnum.TransferFrom]: new FormGroup({
      ...CompanySecurityTransferFromBlock.defineFormControls(),
      date: new FormControl<Date | null>(null, [Validators.required]),
    }),
    [CompanySecurityTransferStepsEnum.TransferTo]: new FormGroup({
      ...CompanySecurityTransferToBlock.defineFormControls(),
      consolidateCertificate: new FormControl<boolean | null>(false, [Validators.required]),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger])
    }),
    [CompanySecurityTransferStepsEnum.AuthorisedSignatories]: new FormGroup({
      authorisedSignatories: AuthorisedSignatoriesStepComponent.defineForm(),
    }),
    [CompanySecurityTransferStepsEnum.Estimate]: new FormGroup({}),
  });

  constructor() {
    super();
    this.setupSteps(CompanySecurityTransferStepsEnum);
    this.updateFormSteps();
    this.redirectAfterSubmit = false;

    this.transferFromForm.controls.shareholder.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFormSteps());

    this.transferToForm.controls.selectShareholder.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200))
      .subscribe(() => this.updateFormSteps());
  }

  override setupFormChangeListeners(): void {
    super.setupFormChangeListeners();
    this.listenChangeDateChanges();
    this.listenChangeDateChange(this.transferFromForm.controls.date, getForm484LateFeeNotification);
  }

  private listenChangeDateChanges() {
    this.transferFromForm.controls.date.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate);
      });
  }

  private updateFormSteps(): void {
    this.steps = ShareTransferSteps.filter((step) => {
      if (step.step === CompanySecurityTransferStepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }
      return true;
    });
  }

  override isAuthorisedSignatoriesStepVisible(): boolean {
    const holderFromGroup = this.transferFromForm.controls.shareholder.value.securityRegistryRecordGroup;
    const holderToType = this.transferToForm.controls.selectShareholder.value.shareholderType;
    const holderToGroup = this.transferToForm.controls.selectShareholder.value.securityRegistryRecordGroup ?? [];

    const isFromIncludeCorporate = holderFromGroup
      && holderFromGroup[0].holders.some((holder) => holder.$type === CorporateHolderModel.$type) || false;

    if (isFromIncludeCorporate) return true;

    if (holderToType === ShareholderTypesEnum.ExistingShareholder) {
      return (holderToGroup != null || holderToGroup != undefined)
        && holderToGroup[0]?.holders.some((holder) => holder.$type === CorporateHolderModel.$type)
        || isFromIncludeCorporate;
    } else if (holderToType === ShareholderTypesEnum.Corporate) {
      return true;
    } else if (holderToType === ShareholderTypesEnum.JointShareholder) {
      return this.transferToForm.controls.selectShareholder.value.joint!.some(holder => holder.$type === CorporateHolderModel.$type)
        || isFromIncludeCorporate;
    }

    return false;
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanySecurityTransfer);
  }

  override setupChange(change: CompanySecurityTransfer = this.formModel) {
    this.shareTransferToBlock()?.disableReadonlyControls();

    if (!this.isEdit && change === this.formModel) {
      return;
    }

    this.setupTransferFromForm(change);
    this.setupTransferToForm(change);
  }

  private setupTransferFromForm(change: CompanySecurityTransfer): void {
    if (!this.transferFromForm.controls.date.value) {
      this.transferFromForm.controls.date.patchValue(change.transferFrom.transactionDate);
    }

    this.shareTransferFromBlock()?.setupChange(change);
  }

  private setupTransferToForm(change: CompanySecurityTransfer): void {
    this.transferToForm.controls.shareCertificateNumber.patchValue(change.transferTo.shareCertificateNumber);
    this.transferToForm.controls.consolidateCertificate.patchValue(change.consolidateCertificate);
    this.shareTransferToBlock()?.setupChange(change);
  }

  override setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;

    if (this.currentStep === CompanySecurityTransferStepsEnum.AuthorisedSignatories) {
      this.authorisedSecurityRegistryRecords = [];

      const holderFromGroup = this.transferFromForm.controls.shareholder.value.securityRegistryRecordGroup;
      const holderToType = this.transferToForm.controls.selectShareholder.value.shareholderType;
      const holding = new SecurityHolding({
        securityType: this.shareTransferFromBlock()?.selectedShareholderShareType!.securityType,
        number: this.transferFromForm.controls.numberIncrease.value!,
        paid: this.transferFromForm.controls.paidIncrease.value!,
        unpaid: this.transferFromForm.controls.unpaidIncrease.value!
      });

      if (holderFromGroup) {
        holderFromGroup.flatMap(g => g.holders).forEach(holder => {
          if (holder.$type === CorporateHolderModel.$type) {
            const newRecord = new SecurityRegistryRecord({
              holders: [holder],
              holdings: [holding]
            });
            newRecord.holders = [holder];
            this.authorisedSecurityRegistryRecords.push(newRecord);
          }
        });
      }

      if (holderToType === ShareholderTypesEnum.ExistingShareholder) {
        const holderToGroup = this.transferToForm.controls.selectShareholder.value.securityRegistryRecordGroup;
        holderToGroup?.forEach(record => this.authorisedSecurityRegistryRecords.push(record));
      } else if (holderToType === ShareholderTypesEnum.Corporate) {
        const entityData = new EntityData({
          name: this.transferToForm.controls.selectShareholder.value.name ?? '',
          registeredAddress: new Address(this.transferToForm.controls.selectShareholder.value.registeredAddress as Partial<Address>)
        });
        const newCorporateSecurityHolder = new CorporateHolderModel({ entityData });
        newCorporateSecurityHolder.isPosted = false;

        const newRecord = new SecurityRegistryRecord({
          holders: [newCorporateSecurityHolder],
          holdings: [holding]
        });
        newRecord.holders = [newCorporateSecurityHolder];
        this.authorisedSecurityRegistryRecords.push(newRecord);
      } else if (holderToType === ShareholderTypesEnum.JointShareholder) {
        const holders = this.transferToForm.controls.selectShareholder.value.joint!
          .filter(holder => holder.$type === CorporateHolderModel.$type)
          .map(holder => holder as CorporateHolderModel);
        holders.forEach(holder => {
          const record = new SecurityRegistryRecord({
            holders: [holder],
            holdings: [holding]
          });
          record.holders = [holder];
          this.authorisedSecurityRegistryRecords.push(record);
        });
      }
    }

    if (this.currentStep === CompanySecurityTransferStepsEnum.Estimate && this.shouldLoadEstimate) {
      this.loadEstimates();
    }
  }

  override buildDocument(): Document | null {
    const companySecurityTransfer: CompanySecurityTransfer = this.buildShareFormChange();
    const doc = new Document({
      changes: [],
      entityId: this.companyChangeData?.entityId,
      type: 'c:484',
      documentId: this.companyChangeData?.documentId ?? Guid.EmptyGuid,
    }) as any as Document;
    doc.changes = [companySecurityTransfer];
    return doc;
  }

  override buildShareFormChange(): CompanySecurityTransfer {
  const change = this.shareTransferFromBlock()!.buildChange();
  const changeDate = this.transferFromForm.controls.date.value!;
  change.changeDate = changeDate;
  change.consolidateCertificate = this.transferToForm.controls.consolidateCertificate.value ?? false;
  change.transferFrom.documentId = this.companyChangeData.documentId ?? null;
  change.transferFrom.transactionDate = changeDate;
  change.transferTo.documentId = this.companyChangeData.documentId ?? null;
  change.transferTo.transactionDate = changeDate;

  const certificateNumber = Number(this.transferToForm.controls.shareCertificateNumber.value)

  change.transferTo.shareCertificateNumber = (certificateNumber + 1).toString();
  change.transferFrom.shareCertificateNumber = certificateNumber.toString();

  return change;
}

  override onShareholdersLoadSuccess(records: SecurityRegistryRecord[], isReload = false): void {
    this.securityRegistryRecords = records;
    this.isLoading = false;

    this.cdr.detectChanges();

    if (isReload) {
      const selectedSecurityRegistryRecord = this.transferFromForm.controls.shareholder.controls.securityRegistryRecordGroup.value;

      if (selectedSecurityRegistryRecord) {
        const matchingRecord = records.find((record) => selectedSecurityRegistryRecord[0].isEqual(record));
        const newValue = matchingRecord
          ? [matchingRecord]
          : null;

        this.transferFromForm.controls.shareholder.controls.securityRegistryRecordGroup.setValue(newValue);
      }
    } else {
      this.setupChange();
      this.prefillForm();
    }
    this.cdr.detectChanges();
  }

  override onEstimateLoadSuccess(estimatedSecurityRegistryRecords: SecurityRegistryRecord[]): void {
    this.estimatedSecurityRegistryRecords = estimatedSecurityRegistryRecords.filter(record => record?.holders?.length > 0);
  }

  override prefillForm(shareGroupView = this.prefillData, shouldPrefillSecurityType = this.shouldPrefillSecurityType): void {
    if (!shareGroupView?.shareholders?.length) {
      // group by class case
      this.prefillData = null;
      return;
    }
    if (!this.shareTransferFromBlock()) return;

    const callRecord = shareGroupView.callRecords[0];
    const record = callRecord?.record ?? null;
    const recordSecurityHoldingId = record?.holdings?.[0]?.securityHoldingId ?? null;

    if (!recordSecurityHoldingId) return;

    const selectedSecurityRegistryGroup = groupSecurityRecordsByName(this.securityRegistryRecords)
      .find((group) => group.some((record) => record?.holdings.some((holder) => holder.securityHoldingId === recordSecurityHoldingId!)));

    if (selectedSecurityRegistryGroup) {
      this.shareTransferFromBlock()?.onSelectCurrentShareholder(selectedSecurityRegistryGroup, false);
      this.transferFromForm.controls.date.setValue(DatepickerHelper.getToday());
      this.transferFromForm.controls.shareholder.controls.securityRegistryRecordGroup.setValue(selectedSecurityRegistryGroup ?? null);

      if (shouldPrefillSecurityType) {
      this.transferFromForm.controls.securityHoldingId.setValue(recordSecurityHoldingId);
      }
    }
  }

  get transferToForm() {
    return this.stepperForm.controls[CompanySecurityTransferStepsEnum.TransferTo];
  }

  get transferFromForm() {
    return this.stepperForm.controls[CompanySecurityTransferStepsEnum.TransferFrom];
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[CompanySecurityTransferStepsEnum.AuthorisedSignatories];
  }
}
